import { AuthForm } from '@/components/auth/auth-form';
import { useProfile } from '@/lib/providers';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  IS_HOOFDNAARHART,
  LOGIN_LEGAL_DOCUMENTS,
  MARKETING_URL,
} from '@/lib/constants';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RichTextViewer } from '@/components/ui/rich-text-viewer';

interface LegalDocument {
  id: string;
  name: string;
  content: string;
}

export default function LoginPage() {
  const { profile, loadingProfile } = useProfile();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [viewingDoc, setViewingDoc] = useState<LegalDocument | null>(null);
  const [legalDocs, setLegalDocs] = useState<LegalDocument[]>([]);


  useEffect(() => {
    // This is the Hartzorg variant's login page. In the HoofdnaarHart build,
    // send visitors to /hoofdhartlogin instead so branding + role gating match.
    if (IS_HOOFDNAARHART) {
      navigate('/hoofdhartlogin', { replace: true });
      return;
    }
    // prevent logged in users from having to login again
    if (profile) navigate('/');
  }, [profile]);

  // Set status bar to light (white icons) on login page, restore on unmount
  useEffect(() => {
    if ((window as any).Capacitor?.getPlatform?.() === 'android') {
      import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
        StatusBar.setStyle({ style: Style.Dark });
      }).catch(() => {});
      return () => {
        import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
          StatusBar.setStyle({ style: Style.Light });
        }).catch(() => {});
      };
    }
  }, []);

  useEffect(() => {
    supabase
      .from('legal_documents')
      .select('id, name, content')
      .is('role_scope', null)
      .in('name', LOGIN_LEGAL_DOCUMENTS)
      .then(({ data }) => {
        if (data) setLegalDocs(data);
      });
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 relative cap-login"
      style={{
        backgroundImage: 'url(/Background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/30" />

      <div className="relative z-10 w-full max-w-md">
        <AuthForm onModeChange={setIsLogin} />
      </div>

      <div className="relative z-10 mt-4 flex flex-wrap justify-center gap-4">
        {legalDocs.map((doc) => (
          <button
            key={doc.id}
            onClick={() => setViewingDoc(doc)}
            className="text-sm text-white/80 hover:text-white underline"
          >
            {doc.name}
          </button>
        ))}
        {/* New tab on purpose: the native builds are a webview with no back
            button, so navigating away would strand someone at the login. */}
        <a
          href={MARKETING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-white/80 hover:text-white underline"
        >
          Meer over ons
        </a>
      </div>

      <Dialog
        open={viewingDoc !== null}
        onOpenChange={(open) => { if (!open) setViewingDoc(null); }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{viewingDoc?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 py-4">
            {viewingDoc?.content ? (
              <RichTextViewer content={viewingDoc.content} />
            ) : (
              <p className="text-muted-foreground text-center py-8">
                Dit document heeft nog geen inhoud.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
